/**
 * AppVersion class for handling semantic versioning in the application.
 *
 * This class provides functionality to create, parse, and compare version numbers
 * following the Semantic Versioning format (major.minor.patch-label).
 *
 * Example usage:
 * ```typescript
 * // Create a version directly
 * const version1 = new AppVersion(1, 2, 3, 'beta');
 *  // "1.2.3-beta"
 *
 * // Parse a version string
 * const version2 = AppVersion.parse('2.0.1');
 *  // 2
 *
 * // Check version components
 * if (version2.isMajor(2) && version2.isMinor(0)) {
 *   
 * }
 * ```
 */
export class AppVersion {
  readonly major: number;
  readonly minor: number;
  readonly patch: number;
  readonly label?: string;

  constructor(major: number, minor: number, patch: number, label?: string) {
    this.major = major;
    this.minor = minor;
    this.patch = patch;
    this.label = label;
  }

  /**
   * Parses a version string into an AppVersion object.
   * Accepts strings in format: "major.minor.patch" or "major.minor.patch-label"
   *
   * @param version - The version string to parse
   * @returns A new AppVersion instance
   * @throws Error if the version string format is invalid
   */
  static parse(version?: string): AppVersion {
    if (!version) {
      throw new Error(`Invalid version format: ${version}`);
    }

    // Use regex to extract version components
    const regex = /^(\d+)\.(\d+)\.(\d+)(?:-(.+))?$/;
    const match = version.match(regex);

    if (!match) {
      throw new Error(`Invalid version format: ${version}`);
    }

    const major = parseInt(match[1], 10);
    const minor = parseInt(match[2], 10);
    const patch = parseInt(match[3], 10);
    const label = match[4];

    return new AppVersion(major, minor, patch, label);
  }

  /**
   * Checks if the major version matches the provided value
   */
  isMajor(value: number): boolean {
    return this.major === value;
  }

  /**
   * Checks if the minor version is greater than or equal to the provided value
   */
  isMajorAtLeast(value: number): boolean {
    return this.major >= value;
  }

  /**
   * Checks if the minor version matches the provided value
   */
  isMinor(value: number): boolean {
    return this.minor === value;
  }

  /**
   * Checks if the patch version matches the provided value
   */
  isPatch(value: number): boolean {
    return this.patch === value;
  }

  /**
   * Returns the string representation of the version
   * Format: "major.minor.patch" or "major.minor.patch-label" if label exists
   */
  toString(): string {
    return this.label
      ? `${this.major}.${this.minor}.${this.patch}-${this.label}`
      : `${this.major}.${this.minor}.${this.patch}`;
  }
}
